import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BellRinging, CheckCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { fmtAUD, fmtPct, MONTH_NAMES } from "../lib/format";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const { user } = useAuth();

  const load = async () => {
    const r = await api.get("/alerts");
    setAlerts(r.data);
  };

  useEffect(() => { load(); }, []);

  const ack = async (id) => {
    await api.post(`/alerts/${id}/ack`);
    toast.success("Alert acknowledged");
    load();
  };

  const canAck = user?.role === "admin" || user?.role === "analyst";

  return (
    <div className="p-6 lg:p-10 max-w-[1600px]">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]"
             style={{ color: "var(--saa-gold)" }}>
          <BellRinging size={14} weight="duotone" />
          Threshold Alerts
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2" data-testid="alerts-title">
          Active Alerts
        </h1>
        <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
          {alerts.length} undercut event{alerts.length !== 1 ? "s" : ""} where a competitor is &gt;15% cheaper than SAA.
        </p>
      </div>

      <div className="space-y-3" data-testid="alerts-list">
        {alerts.length === 0 && (
          <div className="saa-card p-8 text-center" style={{ color: "var(--text-secondary)" }}>
            <CheckCircle size={28} weight="duotone" color="#10B981" className="mx-auto mb-2" />
            No active alerts — SAA is well positioned.
          </div>
        )}
        {alerts.map((a, i) => (
          <motion.div
            key={a.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.02, 0.4) }}
            className="saa-card p-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="pill pill-undercut">{fmtPct(a.gap_pct)} cheaper</span>
                <span className="font-mono text-sm font-semibold">{a.airline_iata}</span>
                <span style={{ color: "var(--text-secondary)" }} className="text-sm">
                  {a.airline_name}
                </span>
                <span className="font-mono text-xs px-2 py-0.5 rounded" style={{ background: "var(--card-bg-2)" }}>
                  {a.cabin_class} · {MONTH_NAMES[a.travel_month - 1]} {a.travel_year}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-3 text-sm">
                <Link to={`/routes/${a.route_id}`} className="font-display font-semibold hover:underline">
                  {a.route_label}
                </Link>
                <span style={{ color: "var(--text-secondary)" }}>
                  SAA <span className="font-mono">{fmtAUD(a.saa_fare)}</span> vs <span className="font-mono">{fmtAUD(a.competitor_fare)}</span>
                </span>
              </div>
            </div>
            {canAck && !a.acknowledged && (
              <Button variant="outline" onClick={() => ack(a.id)} data-testid={`ack-${a.id}`}>
                Acknowledge
              </Button>
            )}
            {a.acknowledged && (
              <span className="text-xs flex items-center gap-1" style={{ color: "var(--success)" }}>
                <CheckCircle size={14} weight="fill" /> Acknowledged
              </span>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
